import type { BoatResponse } from '@sea-snag/shared';
import {
  Badge,
  Button,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@sea-snag/ui';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Ship, X } from 'lucide-react';
import { useState } from 'react';

import { boatsApi } from '../lib/api-client';
import { formatLengthFt } from '../lib/format';
import { queryKeys } from '../lib/query-keys';

// ── Props ──

interface ReferenceBoatSelectorProps {
  orgPid: string;
  selectedPids: string[];
  onChange: (pids: string[]) => void;
  onBoatsChange?: (boats: BoatResponse[]) => void;
  hideBadges?: boolean;
}

// ── Component ──

export function ReferenceBoatSelector({
  orgPid,
  selectedPids,
  onChange,
  onBoatsChange,
  hideBadges,
}: ReferenceBoatSelectorProps) {
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.boats.list(orgPid, 'reference-selector'),
    queryFn: () => boatsApi.list({ limit: 100 }),
  });

  const boats: BoatResponse[] = data?.data ?? [];

  function updateSelection(newPids: string[]) {
    onChange(newPids);
    onBoatsChange?.(boats.filter((b) => newPids.includes(b.id)));
  }

  function toggleBoat(pid: string) {
    if (selectedPids.includes(pid)) {
      updateSelection(selectedPids.filter((p) => p !== pid));
    } else {
      updateSelection([...selectedPids, pid]);
    }
  }

  function removeBoat(pid: string) {
    updateSelection(selectedPids.filter((p) => p !== pid));
  }

  const selectedBoats = boats.filter((b) => selectedPids.includes(b.id));

  function buildSecondaryLine(boat: BoatResponse): string {
    const parts: string[] = [];
    if (boat.model) parts.push(boat.model);
    if (boat.lengthM) parts.push(formatLengthFt(boat.lengthM));
    if (boat.cabins != null) parts.push(`${boat.cabins} cabins`);
    return parts.join(' · ');
  }

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={isLoading}
          >
            <span className="flex items-center gap-2 truncate">
              <Ship className="size-4 shrink-0 text-muted-foreground" />
              {selectedPids.length === 0
                ? 'Select your boats...'
                : `${selectedPids.length} boat${selectedPids.length === 1 ? '' : 's'} selected`}
            </span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          onWheel={(e) => e.stopPropagation()}
        >
          <Command>
            <CommandInput placeholder="Search by name or model..." />
            <CommandList>
              <CommandEmpty>No boats found.</CommandEmpty>
              <CommandGroup>
                {boats.map((boat) => {
                  const isSelected = selectedPids.includes(boat.id);
                  const secondary = buildSecondaryLine(boat);

                  return (
                    <CommandItem
                      key={boat.id}
                      value={`${boat.name} ${boat.model ?? ''}`}
                      onSelect={() => toggleBoat(boat.id)}
                    >
                      <Check
                        className={cn(
                          'mr-2 size-4 shrink-0',
                          isSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{boat.name}</p>
                        {secondary && (
                          <p className="truncate text-xs text-muted-foreground">{secondary}</p>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {!hideBadges && selectedBoats.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedBoats.map((boat) => (
            <Badge key={boat.id} variant="secondary" className="gap-1 pr-1">
              <span className="max-w-[160px] truncate">{boat.name}</span>
              <button
                type="button"
                onClick={() => removeBoat(boat.id)}
                className="ml-0.5 rounded-sm opacity-60 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                aria-label={`Remove ${boat.name}`}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
